import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Input, Popconfirm, Radio, Select, Space, Typography, message, theme } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { Channel, ChannelAgent, conversationsApi } from "@/services/conversations.api";
import { StoreOperator, StoreSeller, storePopulationsApi } from "@/services/store-populations.api";
import { isCloudApi } from "@/services/channel-types";
import { getErrorMessage } from "@/utilities";
import { agentApi } from "@/pages/stores/agent/api";
import { MODOS, ModoDelCanal, esAdminDelStore, guardadoDelAgente, modoDe, modoPermitido } from "./channel-agent";

const { Text } = Typography;

/**
 * El asistente IA del canal (tarea 5.2, D29): su modo `off | assist | auto`, el usuario con el que
 * actúa y el nombre con el que firma. Lo cambia sólo el admin del Store; el resto lo ve.
 */
export const ChannelAgentCard: React.FC<{ channel: Channel }> = ({ channel }) => {
  const { token } = theme.useToken();
  const { data: identity } = useGetIdentity<any>();
  const editable = esAdminDelStore(identity, channel.storeCode);
  const esCloud = isCloudApi(channel.type);

  const [actual, setActual] = useState<ChannelAgent | null>(null);
  const [operadores, setOperadores] = useState<StoreOperator[]>([]);
  const [modo, setModo] = useState<ModoDelCanal>("off");
  const [userId, setUserId] = useState<number | null>(null);
  const [nombre, setNombre] = useState("");
  // D58: el seller por el que vende el canal. Vacío: por el store.
  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [sellerCode, setSellerCode] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [saving, setSaving] = useState(false);

  const aplicar = (a: ChannelAgent | null) => {
    setActual(a);
    setModo(modoDe(a));
    if (a) {
      setUserId(a.userId);
      setNombre(a.displayName);
      setSellerCode(a.sellerCode ?? null);
    }
  };

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    Promise.all([
      conversationsApi.getChannelAgent(channel.channelId).catch(() => null),
      storePopulationsApi.listOperators(channel.storeCode).catch(() => [] as StoreOperator[]),
      storePopulationsApi.listSellers(channel.storeCode).catch(() => [] as StoreSeller[]),
      // Para proponer, en un alta, el usuario con el que corre el pod y el nombre del store.
      agentApi.estado(channel.storeCode).catch(() => null),
      agentApi.config(channel.storeCode).catch(() => null),
    ])
      .then(([a, ops, sel, estado, config]) => {
        if (!vivo) return;
        setOperadores(ops);
        setSellers(sel);
        aplicar(a);
        if (!a) {
          setUserId(estado?.agentUserId ?? null);
          setNombre(config?.displayName ?? "");
        }
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [channel.channelId, channel.storeCode]);

  const guardar = async () => {
    const g = guardadoDelAgente(modo, { userId, displayName: nombre, sellerCode }, actual, esCloud);
    if (g.accion === "nada") {
      message.info("No hay cambios en el asistente");
      return;
    }
    if (g.accion === "error") {
      message.error(g.motivo);
      return;
    }
    setSaving(true);
    try {
      aplicar(await conversationsApi.setChannelAgent(channel.channelId, g.body));
      message.success(g.body.enabled ? "Asistente guardado" : "Asistente apagado en este canal");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const quitar = async () => {
    setSaving(true);
    try {
      await conversationsApi.removeChannelAgent(channel.channelId);
      setActual(null);
      setModo("off");
      message.success("Asistente quitado del canal");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const ayuda = MODOS.find((m) => m.value === modo)?.ayuda;
  const operador = operadores.find((o) => o.userId === (actual?.userId ?? userId));

  return (
    <Card
      loading={cargando}
      title={<span style={{ fontSize: 14 }}>Asistente IA</span>}
      style={{ marginBottom: 16, borderColor: token.colorBorder }}
      styles={{ header: { minHeight: 38, padding: "0 16px" } }}
    >
      {editable ? (
        <Flex vertical gap={12} style={{ maxWidth: 560 }}>
          <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)}>
            {MODOS.map((m) => (
              <Radio.Button key={m.value} value={m.value} disabled={!modoPermitido(m.value, esCloud)}>
                {m.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          <Text type="secondary">
            {ayuda}
            {!esCloud && " El modo automático es sólo para canales de WhatsApp Cloud API."}
          </Text>

          {modo !== "off" && (
            <>
              <Flex vertical gap={4}>
                <Text>Usuario del asistente</Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  value={userId ?? undefined}
                  onChange={setUserId}
                  placeholder="El usuario con el que corre el asistente"
                  options={operadores.map((o) => ({ value: o.userId, label: o.email ? `${o.name} · ${o.email}` : o.name }))}
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text>Nombre con el que firma</Text>
                <Input value={nombre} maxLength={100} onChange={(e) => setNombre(e.target.value)} placeholder="Asistente de la tienda" />
              </Flex>
              {sellers.length > 1 && (
                <Flex vertical gap={4}>
                  <Text>Sucursal del canal</Text>
                  <Select
                    allowClear
                    value={sellerCode ?? undefined}
                    onChange={(v) => setSellerCode(v ?? null)}
                    placeholder="Todas las sucursales del store"
                    options={sellers.map((s) => ({ value: s.sellerCode, label: s.name }))}
                  />
                  <Text type="secondary">
                    Con una sucursal, el asistente responde sólo con sus datos, su precio y su stock, y arma los
                    presupuestos con ella. Lo de otra sucursal lo pasa a una persona.
                  </Text>
                </Flex>
              )}
            </>
          )}

          <Space>
            <Button type="primary" icon={<CheckOutlined />} iconPosition="end" loading={saving} onClick={guardar}>
              Guardar asistente
            </Button>
            {actual && (
              <Popconfirm
                title="¿Quitar el asistente de este canal?"
                description="Se borra su configuración en el canal. Para pausarlo alcanza con Apagado."
                okText="Quitar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={quitar}
              >
                <Button danger disabled={saving}>
                  Quitar
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Flex>
      ) : (
        <Flex vertical gap={4}>
          <Text>
            {MODOS.find((m) => m.value === modoDe(actual))?.label}
            {actual && ` · ${actual.displayName}${operador ? ` (${operador.name})` : ""}`}
            {actual?.sellerCode &&
              ` · ${sellers.find((s) => s.sellerCode === actual.sellerCode)?.name ?? actual.sellerCode}`}
          </Text>
          <Text type="secondary">Sólo el administrador del store puede cambiarlo.</Text>
        </Flex>
      )}
    </Card>
  );
};
